import _ from 'lodash';

function find(map, keyToFind) {
  for (const [key, value] of map) {
    if (_.isEqual(keyToFind, key)) {
      return value;
    }
  }
}

type Function0<R> = () => R;
type Function1<T1, R> = (t1: T1) => R;
type Function2<T1, T2, R> = (t1: T1, t2: T2) => R;
type Function3<T1, T2, T3, R> = (t1: T1, t2: T2, t3: T3) => R;

interface MemoizedPartial {
  // arity 0
  <R>(func: Function0<R>): Function0<R>;

  // arity 1
  <T1, R>(func: Function1<T1, R>): Function1<T1, R>;
  <T1, R>(func: Function1<T1, R>, arg1: T1): Function0<R>;

  // arity 2
  <T1, T2, R>(func: Function2<T1, T2, R>): Function2<T1, T2, R>;
  <T1, T2, R>(func: Function2<T1, T2, R>, arg1: T1): Function1<T2, R>;
  <T1, T2, R>(func: Function2<T1, T2, R>, arg1: T1, arg2: T2): Function0<R>;

  // arity 3
  <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>): Function3<T1, T2, T3, R>;
  <T1, T2, T3, R>(func: Function3<T1, T2, T3, R>, arg1: T1): Function2<
    T2,
    T3,
    R
  >;
  <T1, T2, T3, R>(
    func: Function3<T1, T2, T3, R>,
    arg1: T1,
    arg2: T2,
  ): Function1<T3, R>;
  <T1, T2, T3, R>(
    func: Function3<T1, T2, T3, R>,
    arg1: T1,
    arg2: T2,
    arg3: T3,
  ): Function0<R>;
}

export const memoizedPartial: MemoizedPartial = (fn, ...args) => {
  let memoizedPartialFn;

  if (fn._memoizeCache) {
    memoizedPartialFn = find(fn._memoizeCache, args);
  } else {
    fn._memoizeCache = new Map();
  }

  if (!memoizedPartialFn) {
    memoizedPartialFn = _.partial.apply(null, [fn, ...args]);
    fn._memoizeCache.set(args, memoizedPartialFn);
  }

  return memoizedPartialFn;
};
